// Enhanced SlideShowHeaderWaveForm.js - Direct Replacement
import React, { useState } from 'react';
import { Info, Type, Image, Video, Palette, Settings, Zap } from 'lucide-react';
import ColorPicker from '../../shared/ColorPicker';
import MediaLibrary from '../../shared/MediaUploader';

const SlideShowHeaderWaveForm = ({ content, updateContent, pageSlug }) => {
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [currentImageField, setCurrentImageField] = useState(null);
  const [currentMediaType, setCurrentMediaType] = useState('image');

  const handleSelectImage = (imageUrl) => {
    if (currentImageField) {
      updateContent(currentImageField, imageUrl);
      setShowMediaLibrary(false);
      setCurrentImageField(null);
    }
  };

  const openMediaLibrary = (fieldName, mediaType = 'image') => {
    setCurrentImageField(fieldName);
    setCurrentMediaType(mediaType);
    setShowMediaLibrary(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center mb-6">
        <div className="bg-gradient-to-r from-blue-100 to-purple-100 p-3 rounded-lg mr-4">
          <Image className="h-6 w-6 text-blue-600" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Hero Banner Section</h3>
          <p className="text-sm text-gray-500 mt-1">Create an impactful hero section with customizable content and backgrounds</p>
        </div>
      </div>
      
      {showMediaLibrary ? (
        <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <div className="bg-blue-100 p-2 rounded-lg mr-3">
                <Image className="h-5 w-5 text-blue-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900">Media Library</h4>
            </div>
            <button 
              onClick={() => setShowMediaLibrary(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
          <MediaLibrary 
            onUploadComplete={handleSelectImage} 
            type={currentMediaType}
            pageSlug={pageSlug} 
          />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Content and Text Settings */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center mb-6">
              <div className="bg-blue-100 p-2 rounded-lg mr-3">
                <Type className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Content & Text</h4>
                <p className="text-sm text-gray-500 mt-1">Configure the main text content and styling</p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Hero Title</label>
                <input
                  type="text"
                  value={content?.title || 'Paper Bag & Box <span>Custom Packaging</span>'}
                  onChange={(e) => updateContent('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Enter your hero title"
                />
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <span className="font-medium">Tip:</span> Use <code className="bg-blue-100 px-1 rounded">&lt;span&gt;text&lt;/span&gt;</code> to highlight parts of the title with the accent color
                  </p>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Subtitle</label>
                <textarea
                  value={content?.subtitle || 'Elevate your brand with premium quality custom bags and boxes. Perfect for businesses of all sizes.'}
                  onChange={(e) => updateContent('subtitle', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  rows="3"
                  placeholder="Enter subtitle or description"
                />
                <p className="text-xs text-gray-500">Supporting text that appears below the main title</p>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Highlight Color</label>
                <div className="flex items-center space-x-3">
                  <ColorPicker
                    color={content?.highlightColor || '#f59e0b'}
                    onChange={(color) => updateContent('highlightColor', color)}
                  />
                  
                </div>
                <p className="text-xs text-gray-500">Color used for highlighted text within &lt;span&gt; tags</p>
              </div>
            </div>
          </div>

          {/* Background Settings */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center mb-6">
              <div className="bg-purple-100 p-2 rounded-lg mr-3">
                <Image className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Background Settings</h4>
                <p className="text-sm text-gray-500 mt-1">Configure background images, videos, and styling</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Background Type Selection */}
              <div>
                <h5 className="font-medium text-gray-900 mb-4">Background Type</h5>
                <div className="flex space-x-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex items-center h-5">
                      <input
                        type="radio"
                        id="bg-image"
                        name="backgroundType"
                        checked={!content?.videoBackground}
                        onChange={() => updateContent('videoBackground', false)}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                      />
                    </div>
                    <div className="flex-1">
                      <label htmlFor="bg-image" className="text-sm font-medium text-gray-700">Image Background</label>
                      <p className="text-xs text-gray-500 mt-1">Use static images for desktop and mobile</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="flex items-center h-5">
                      <input
                        type="radio"
                        id="bg-video"
                        name="backgroundType"
                        checked={!!content?.videoBackground}
                        onChange={() => updateContent('videoBackground', true)}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                      />
                    </div>
                    <div className="flex-1">
                      <label htmlFor="bg-video" className="text-sm font-medium text-gray-700">Video Background</label>
                      <p className="text-xs text-gray-500 mt-1">Use video files for dynamic backgrounds</p>
                    </div>
                  </div>
                </div>
              </div>

              {!content?.videoBackground ? (
                // Image Background Options
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center mb-4">
                    <div className="bg-green-100 p-2 rounded-lg mr-3">
                      <Image className="h-4 w-4 text-green-600" />
                    </div>
                    <h5 className="font-medium text-gray-900">Image Backgrounds</h5>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Desktop Background</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={content?.backgroundImage || '/images/s1.jpg'}
                          onChange={(e) => updateContent('backgroundImage', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="/images/hero-desktop.jpg"
                        />
                        <button
                          type="button"
                          onClick={() => openMediaLibrary('backgroundImage')}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors whitespace-nowrap"
                        >
                          Browse
                        </button>
                      </div>
                      {content?.backgroundImage && (
                        <div className="mt-3 relative h-24 w-full overflow-hidden rounded-lg border border-gray-200">
                          <img 
                            src={content.backgroundImage} 
                            alt="Desktop Background Preview" 
                            className="h-full w-full object-cover"
                          />
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Mobile Background</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={content?.mobileBackgroundImage || '/images/s3.jpg'}
                          onChange={(e) => updateContent('mobileBackgroundImage', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="/images/hero-mobile.jpg"
                        />
                        <button
                          type="button"
                          onClick={() => openMediaLibrary('mobileBackgroundImage')}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors whitespace-nowrap"
                        >
                          Browse
                        </button>
                      </div>
                      {content?.mobileBackgroundImage && (
                        <div className="mt-3 relative h-24 w-full overflow-hidden rounded-lg border border-gray-200">
                          <img 
                            src={content.mobileBackgroundImage} 
                            alt="Mobile Background Preview" 
                            className="h-full w-full object-cover"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                // Video Background Options
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center mb-4">
                    <div className="bg-red-100 p-2 rounded-lg mr-3">
                      <Video className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">Video Backgrounds</h5>
                      <p className="text-sm text-gray-500">Configure video files and playback settings</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Desktop Video</label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={content?.backgroundVideo || ''}
                            onChange={(e) => updateContent('backgroundVideo', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            placeholder="/videos/hero-desktop.mp4"
                          />
                          <button
                            type="button"
                            onClick={() => openMediaLibrary('backgroundVideo', 'video')}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors whitespace-nowrap"
                          >
                            Browse
                          </button>
                        </div>
                        {content?.backgroundVideo && (
                          <div className="mt-3 relative h-24 w-full overflow-hidden rounded-lg border border-gray-200">
                            <video 
                              src={content.backgroundVideo} 
                              className="h-full w-full object-cover"
                              controls
                            />
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Mobile Video</label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={content?.mobileBackgroundVideo || ''}
                            onChange={(e) => updateContent('mobileBackgroundVideo', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            placeholder="/videos/hero-mobile.mp4"
                          />
                          <button
                            type="button"
                            onClick={() => openMediaLibrary('mobileBackgroundVideo', 'video')}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors whitespace-nowrap"
                          >
                            Browse
                          </button>
                        </div>
                        {content?.mobileBackgroundVideo && (
                          <div className="mt-3 relative h-24 w-full overflow-hidden rounded-lg border border-gray-200">
                            <video 
                              src={content.mobileBackgroundVideo} 
                              className="h-full w-full object-cover"
                              controls
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Video Poster (Fallback Image)</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={content?.videoPoster || ''}
                          onChange={(e) => updateContent('videoPoster', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="/images/video-poster.jpg"
                        />
                        <button
                          type="button"
                          onClick={() => openMediaLibrary('videoPoster')}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors whitespace-nowrap"
                        >
                          Browse
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">Image shown before video loads or if video fails to play</p>
                    </div>

                    <div>
                      <h6 className="font-medium text-gray-900 mb-3">Video Playback Settings</h6>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="flex items-start space-x-3">
                          <div className="flex items-center h-5">
                            <input
                              type="checkbox"
                              id="video-autoplay"
                              checked={content?.videoAutoplay !== false}
                              onChange={(e) => updateContent('videoAutoplay', e.target.checked)}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            />
                          </div>
                          <div className="flex-1">
                            <label htmlFor="video-autoplay" className="text-sm font-medium text-gray-700">Autoplay</label>
                            <p className="text-xs text-gray-500 mt-1">Start playing automatically</p>
                          </div>
                        </div>

                        <div className="flex items-start space-x-3">
                          <div className="flex items-center h-5">
                            <input
                              type="checkbox"
                              id="video-loop"
                              checked={content?.videoLoop !== false}
                              onChange={(e) => updateContent('videoLoop', e.target.checked)}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            />
                          </div>
                          <div className="flex-1">
                            <label htmlFor="video-loop" className="text-sm font-medium text-gray-700">Loop</label>
                            <p className="text-xs text-gray-500 mt-1">Repeat video continuously</p>
                          </div>
                        </div>

                        <div className="flex items-start space-x-3">
                          <div className="flex items-center h-5">
                            <input
                              type="checkbox"
                              id="video-muted"
                              checked={content?.videoMuted !== false}
                              onChange={(e) => updateContent('videoMuted', e.target.checked)}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            />
                          </div>
                          <div className="flex-1">
                            <label htmlFor="video-muted" className="text-sm font-medium text-gray-700">Muted</label>
                            <p className="text-xs text-gray-500 mt-1">Start without sound</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Common Background Settings */}
              <div className="border-t border-gray-200 pt-6">
                <h5 className="font-medium text-gray-900 mb-4">Background Styling</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Overlay Color</label>
                    <div className="flex items-center space-x-3">
                      <ColorPicker
                        color={content?.overlayColor || 'rgba(0,0,0,0.5)'}
                        onChange={(color) => updateContent('overlayColor', color)}
                        allowAlpha={true}
                      />
                      
                    </div>
                    <p className="text-xs text-gray-500">Dark overlay to improve text readability</p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Section Height</label>
                    <input
                      type="text"
                      value={content?.height || '100vh'}
                      onChange={(e) => updateContent('height', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="100vh"
                    />
                    <p className="text-xs text-gray-500">e.g., 100vh, 800px</p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Minimum Height</label>
                    <input
                      type="text"
                      value={content?.minHeight || '600px'}
                      onChange={(e) => updateContent('minHeight', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="600px"
                    />
                    <p className="text-xs text-gray-500">Minimum height on smaller screens</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Primary Button Settings */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center mb-6">
              <div className="bg-orange-100 p-2 rounded-lg mr-3">
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Primary Action Button</h4>
                <p className="text-sm text-gray-500 mt-1">Configure the main call-to-action button</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Button Text</label>
                  <input
                    type="text"
                    value={content?.primaryButtonText || 'Customize Products'}
                    onChange={(e) => updateContent('primaryButtonText', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Get Started"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Button Link</label>
                  <input
                    type="text"
                    value={content?.primaryButtonLink || '/products'}
                    onChange={(e) => updateContent('primaryButtonLink', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="/products"
                  />
                  <p className="text-xs text-gray-500">URL or page path for the button link</p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Button Color</label>
                <div className="flex items-center space-x-3">
                  <ColorPicker
                    color={content?.primaryButtonColor || '#d97706'}
                    onChange={(color) => updateContent('primaryButtonColor', color)}
                  />
                 
                </div>
                <p className="text-xs text-gray-500">Background color for the primary button</p>
              </div>
            </div>
          </div>
          
          {/* Secondary Button Settings */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center mb-6">
              <div className="bg-green-100 p-2 rounded-lg mr-3">
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Secondary Action Button</h4>
                <p className="text-sm text-gray-500 mt-1">Configure the secondary call-to-action button (usually for contact)</p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Button Text</label>
                    <input
                      type="text"
                      value={content?.secondaryButtonText || 'Call to Order'}
                      onChange={(e) => updateContent('secondaryButtonText', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Contact Us"
                    />
                    <p className="text-xs text-gray-500">Leave empty to hide the secondary button</p>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Button Link</label>
                    <input
                      type="text"
                      value={content?.secondaryButtonLink || 'tel:+18333333101'}
                      onChange={(e) => updateContent('secondaryButtonLink', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="tel:+1234567890"
                    />
                    <p className="text-xs text-gray-500">Use tel: for phone numbers, mailto: for emails</p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Phone Number Display</label>
                    <input
                      type="text"
                      value={content?.phoneNumber || '+18333333101'}
                      onChange={(e) => updateContent('phoneNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="+1 (800) 123-4567"
                    />
                    <p className="text-xs text-gray-500">Formatted phone number shown to users</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Button Color</label>
                    <div className="flex items-center space-x-3">
                      <ColorPicker
                        color={content?.secondaryButtonColor || '#16a34a'}
                        onChange={(color) => updateContent('secondaryButtonColor', color)}
                      />
                      <div 
                        className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                        style={{ backgroundColor: content?.secondaryButtonColor || '#16a34a' }}
                      />
                    </div>
                    <p className="text-xs text-gray-500">Background color for the secondary button</p>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="flex items-center h-5">
                      <input
                        type="checkbox"
                        id="show-phone-icon"
                        checked={content?.showPhoneIcon !== false}
                        onChange={(e) => updateContent('showPhoneIcon', e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      />
                    </div>
                    <div className="flex-1">
                      <label htmlFor="show-phone-icon" className="text-sm font-medium text-gray-700">Show Phone Icon</label>
                      <p className="text-xs text-gray-500 mt-1">Display phone icon next to button text</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Advanced Settings */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center mb-6">
              <div className="bg-yellow-100 p-2 rounded-lg mr-3">
                <Settings className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Advanced Settings</h4>  
                <p className="text-sm text-gray-500 mt-1">Configure additional options for the hero section</p>
              </div>
            </div>      
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Section ID</label>
                <input
                  type="text"
                  value={content?.sectionId || 'hero-banner'}
                  onChange={(e) => updateContent('sectionId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="hero-banner"
                />
                <p className="text-xs text-gray-500">Unique ID for this section (used for linking)</p>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Custom CSS Classes</label> 
                <input
                  type="text"
                  value={content?.customClasses || ''}
                  onChange={(e) => updateContent('customClasses', e.target.value)}  
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="custom-hero-section"
                />
                <p className="text-xs text-gray-500">Additional CSS classes for custom styling</p>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Custom Styles</label>
                <textarea
                  value={content?.customStyles || ''}
                  onChange={(e) => updateContent('customStyles', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  rows="3"  
                  placeholder="background-color: #f0f0f0; padding: 20px;"
                />
                <p className="text-xs text-gray-500">Custom inline styles for this section</p>
              </div>
            </div>
          </div>
        </div>
    
            )}
          </div>
        );
      };
export default SlideShowHeaderWaveForm;







// // src/components/admin/page-management/section-forms/SlideShowHeaderWaveForm.js
// import React, { useState } from 'react';
// import { Info } from 'lucide-react';
// import ColorPicker from '../../shared/ColorPicker';
// import MediaLibrary from '../../shared/MediaUploader';

// const SlideShowHeaderWaveForm = ({ content, updateContent, pageSlug }) => {
//   const [showMediaLibrary, setShowMediaLibrary] = useState(false);
//   const [currentImageField, setCurrentImageField] = useState(null);
//   const [currentMediaType, setCurrentMediaType] = useState('image'); // Add this line

//   const handleSelectImage = (imageUrl) => {
//     if (currentImageField) {
//       updateContent(currentImageField, imageUrl);
//       setShowMediaLibrary(false);
//       setCurrentImageField(null);
//     }
//   };

//   // const openMediaLibrary = (fieldName) => {
//   //   setCurrentImageField(fieldName);
//   //   setShowMediaLibrary(true);
//   // };

//   // Updated to include mediaType parameter
//   const openMediaLibrary = (fieldName, mediaType = 'image') => {
//     setCurrentImageField(fieldName);
//     setCurrentMediaType(mediaType); // Set the current media type
//     setShowMediaLibrary(true);
//   };

//   return (
//     <div className="space-y-6">
//       <h3 className="text-lg font-semibold">Hero Banner Section</h3>
      
//       {showMediaLibrary ? (
//         <div className="bg-white rounded-lg shadow-lg p-4">
//           <div className="flex justify-between items-center mb-4">
//             <h4 className="text-lg font-medium">Media Library</h4>
//             <button 
//               onClick={() => setShowMediaLibrary(false)}
//               className="text-gray-500 hover:text-gray-700"
//             >
//               Close
//             </button>
//           </div>
//           {/* <MediaLibrary 
//             onSelect={handleSelectImage} 
//             pageSlug={pageSlug} 
//           /> */}
//           {/* Pass the media type to the MediaUploader */}
//           <MediaLibrary 
//             onUploadComplete={handleSelectImage} 
//             type={currentMediaType} // This is the important change
//             pageSlug={pageSlug} 
//           />
//         </div>
//       ) : (
//         <>
//           {/* Content and Text Settings */}
//           <div className="bg-gray-50 p-4 rounded-md">
//             <h4 className="font-medium mb-4">Content</h4>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Title</label>
//               <input
//                 type="text"
//                 value={content?.title || 'Paper Bag & Box <span>Custom Packaging</span>'}
//                 onChange={(e) => updateContent('title', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 Use &lt;span&gt;text&lt;/span&gt; to highlight parts of the title
//               </p>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Subtitle</label>
//               <textarea
//                 value={content?.subtitle || 'Elevate your brand with premium quality custom bags and boxes. Perfect for businesses of all sizes.'}
//                 onChange={(e) => updateContent('subtitle', e.target.value)}
//                 className="w-full p-2 border rounded"
//                 rows="3"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Highlight Color</label>
//               <ColorPicker
//                 color={content?.highlightColor || '#f59e0b'}
//                 onChange={(color) => updateContent('highlightColor', color)}
//               />
//             </div>
//           </div>

//           {/* Background Settings */}
//           <div className="bg-gray-50 p-4 rounded-md">
//             <h4 className="font-medium mb-4">Background Settings</h4>


//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Background Type</label>
//               <div className="flex space-x-4">
//                 <label className="inline-flex items-center">
//                   <input
//                     type="radio"
//                     name="backgroundType"
//                     checked={!content?.videoBackground}
//                     onChange={() => updateContent('videoBackground', false)}
//                     className="mr-2"
//                   />
//                   <span className="text-sm">Image</span>
//                 </label>
//                 <label className="inline-flex items-center">
//                   <input
//                     type="radio"
//                     name="backgroundType"
//                     checked={!!content?.videoBackground}
//                     onChange={() => updateContent('videoBackground', true)}
//                     className="mr-2"
//                   />
//                   <span className="text-sm">Video</span>
//                 </label>
//               </div>
//             </div>


//             {!content?.videoBackground ? (
//               // Image background options (your existing code)
//               <>
//                 <div className="mb-4">
//                   <label className="block text-sm font-medium mb-1">Desktop Background Image</label>
//                   {/* Existing image upload code */}
//                 </div>
                
//                 <div className="mb-4">
//                   <label className="block text-sm font-medium mb-1">Mobile Background Image</label>
//                   {/* Existing mobile image upload code */}
//                 </div>
//               </>
//             ) : (
//               // Video background options
//               <>
//                 <div className="mb-4">
//                   <label className="block text-sm font-medium mb-1">Desktop Background Video</label>
//                   <div className="flex items-center">
//                     <input
//                       type="text"
//                       value={content?.backgroundVideo || ''}
//                       onChange={(e) => updateContent('backgroundVideo', e.target.value)}
//                       className="w-full p-2 border rounded"
//                     />
//                     <button
//                       type="button"
//                       onClick={() => openMediaLibrary('backgroundVideo', 'video')}
//                       className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
//                     >
//                       Browse
//                     </button>
//                   </div>
//                   {content?.backgroundVideo && (
//                     <div className="mt-2 relative h-20 w-full overflow-hidden rounded border">
//                       <video 
//                         src={content.backgroundVideo} 
//                         className="h-full w-full object-cover"
//                         controls
//                       />
//                     </div>
//                   )}
//                 </div>
                
//                 <div className="mb-4">
//                   <label className="block text-sm font-medium mb-1">Mobile Background Video</label>
//                   <div className="flex items-center">
//                     <input
//                       type="text"
//                       value={content?.mobileBackgroundVideo || ''}
//                       onChange={(e) => updateContent('mobileBackgroundVideo', e.target.value)}
//                       className="w-full p-2 border rounded"
//                     />
//                     <button
//                       type="button"
//                       onClick={() => openMediaLibrary('mobileBackgroundVideo', 'video')}
//                       className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
//                     >
//                       Browse
//                     </button>
//                   </div>
//                   {content?.mobileBackgroundVideo && (
//                     <div className="mt-2 relative h-20 w-full overflow-hidden rounded border">
//                       <video 
//                         src={content.mobileBackgroundVideo} 
//                         className="h-full w-full object-cover"
//                         controls
//                       />
//                     </div>
//                   )}
//                 </div>
                
//                 <div className="mb-4">
//                   <label className="block text-sm font-medium mb-1">Video Poster Image (Fallback)</label>
//                   <div className="flex items-center">
//                     <input
//                       type="text"
//                       value={content?.videoPoster || ''}
//                       onChange={(e) => updateContent('videoPoster', e.target.value)}
//                       className="w-full p-2 border rounded"
//                     />
//                     <button
//                       type="button"
//                       onClick={() => openMediaLibrary('videoPoster')}
//                       className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
//                     >
//                       Browse
//                     </button>
//                   </div>
//                 </div>
                
//                 <div className="grid grid-cols-3 gap-4">
//                   <div className="mb-4">
//                     <label className="flex items-center">
//                       <input
//                         type="checkbox"
//                         checked={content?.videoAutoplay !== false}
//                         onChange={(e) => updateContent('videoAutoplay', e.target.checked)}
//                         className="mr-2"
//                       />
//                       <span className="text-sm">Autoplay</span>
//                     </label>
//                   </div>
//                   <div className="mb-4">
//                     <label className="flex items-center">
//                       <input
//                         type="checkbox"
//                         checked={content?.videoLoop !== false}
//                         onChange={(e) => updateContent('videoLoop', e.target.checked)}
//                         className="mr-2"
//                       />
//                       <span className="text-sm">Loop</span>
//                     </label>
//                   </div>
//                   <div className="mb-4">
//                     <label className="flex items-center">
//                       <input
//                         type="checkbox"
//                         checked={content?.videoMuted !== false}
//                         onChange={(e) => updateContent('videoMuted', e.target.checked)}
//                         className="mr-2"
//                       />
//                       <span className="text-sm">Muted</span>
//                     </label>
//                   </div>
//                 </div>
//               </>
//             )}
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Desktop Background Image</label>
//               <div className="flex items-center">
//                 <input
//                   type="text"
//                   value={content?.backgroundImage || '/images/s1.jpg'}
//                   onChange={(e) => updateContent('backgroundImage', e.target.value)}
//                   className="w-full p-2 border rounded"
//                 />
//                 <button
//                   type="button"
//                   onClick={() => openMediaLibrary('backgroundImage')}
//                   className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
//                 >
//                   Browse
//                 </button>
//               </div>
//               {content?.backgroundImage && (
//                 <div className="mt-2 relative h-20 w-full overflow-hidden rounded border">
//                   <img 
//                     src={content.backgroundImage} 
//                     alt="Background Preview" 
//                     className="h-full w-full object-cover"
//                   />
//                 </div>
//               )}
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Mobile Background Image</label>
//               <div className="flex items-center">
//                 <input
//                   type="text"
//                   value={content?.mobileBackgroundImage || '/images/s3.jpg'}
//                   onChange={(e) => updateContent('mobileBackgroundImage', e.target.value)}
//                   className="w-full p-2 border rounded"
//                 />
//                 <button
//                   type="button"
//                   onClick={() => openMediaLibrary('mobileBackgroundImage')}
//                   className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
//                 >
//                   Browse
//                 </button>
//               </div>
//               {content?.mobileBackgroundImage && (
//                 <div className="mt-2 relative h-20 w-full overflow-hidden rounded border">
//                   <img 
//                     src={content.mobileBackgroundImage} 
//                     alt="Mobile Background Preview" 
//                     className="h-full w-full object-cover"
//                   />
//                 </div>
//               )}
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Overlay Color</label>
//               <ColorPicker
//                 color={content?.overlayColor || 'rgba(0,0,0,0.5)'}
//                 onChange={(color) => updateContent('overlayColor', color)}
//                 allowAlpha={true}
//               />
//             </div>
            
//             <div className="grid grid-cols-2 gap-4 mb-4">
//               <div>
//                 <label className="block text-sm font-medium mb-1">Height</label>
//                 <input
//                   type="text"
//                   value={content?.height || '100vh'}
//                   onChange={(e) => updateContent('height', e.target.value)}
//                   className="w-full p-2 border rounded"
//                   placeholder="e.g., 100vh, 800px"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Min Height</label>
//                 <input
//                   type="text"
//                   value={content?.minHeight || '600px'}
//                   onChange={(e) => updateContent('minHeight', e.target.value)}
//                   className="w-full p-2 border rounded"
//                   placeholder="e.g., 600px"
//                 />
//               </div>
//             </div>
//           </div>
          
//           {/* Primary Button Settings */}
//           <div className="bg-gray-50 p-4 rounded-md">
//             <h4 className="font-medium mb-4">Primary Button</h4>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Text</label>
//               <input
//                 type="text"
//                 value={content?.primaryButtonText || 'Customize Products'}
//                 onChange={(e) => updateContent('primaryButtonText', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Link</label>
//               <input
//                 type="text"
//                 value={content?.primaryButtonLink || '/products'}
//                 onChange={(e) => updateContent('primaryButtonLink', e.target.value)}
//                 className="w-full p-2 border rounded"
//                 placeholder="e.g., /products"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Color</label>
//               <ColorPicker
//                 color={content?.primaryButtonColor || '#d97706'}
//                 onChange={(color) => updateContent('primaryButtonColor', color)}
//               />
//             </div>
//           </div>
          
//           {/* Secondary Button Settings */}
//           <div className="bg-gray-50 p-4 rounded-md">
//             <h4 className="font-medium mb-4">Secondary Button</h4>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Text</label>
//               <input
//                 type="text"
//                 value={content?.secondaryButtonText || 'Call to Order'}
//                 onChange={(e) => updateContent('secondaryButtonText', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//               <p className="text-xs text-gray-500 mt-1">Leave empty to hide the secondary button</p>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Link</label>
//               <input
//                 type="text"
//                 value={content?.secondaryButtonLink || 'tel:+18333333101'}
//                 onChange={(e) => updateContent('secondaryButtonLink', e.target.value)}
//                 className="w-full p-2 border rounded"
//                 placeholder="e.g., tel:+18333333101"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Phone Number</label>
//               <input
//                 type="text"
//                 value={content?.phoneNumber || '+18333333101'}
//                 onChange={(e) => updateContent('phoneNumber', e.target.value)}
//                 className="w-full p-2 border rounded"
//                 placeholder="e.g., +18333333101"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Color</label>
//               <ColorPicker
//                 color={content?.secondaryButtonColor || '#16a34a'}
//                 onChange={(color) => updateContent('secondaryButtonColor', color)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="flex items-center">
//                 <input
//                   type="checkbox"
//                   checked={content?.showPhoneIcon !== false}
//                   onChange={(e) => updateContent('showPhoneIcon', e.target.checked)}
//                   className="mr-2"
//                 />
//                 <span className="text-sm">Show Phone Icon</span>
//               </label>
//             </div>
//           </div>
          
//           {/* Advanced Settings */}
//           <div className="bg-gray-50 p-4 rounded-md">
//             <div className="flex justify-between items-center mb-4">
//               <h4 className="font-medium">Advanced Settings</h4>
//               <div className="text-blue-500">
//                 <Info size={16} />
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="flex items-center">
//                 <input
//                   type="checkbox"
//                   checked={content?.showWaveSeparator !== false}
//                   onChange={(e) => updateContent('showWaveSeparator', e.target.checked)}
//                   className="mr-2"
//                 />
//                 <span className="text-sm">Show Wave Separator</span>
//               </label>
//             </div>
            
//             {content?.showWaveSeparator !== false && (
//               <div className="mb-4">
//                 <label className="block text-sm font-medium mb-1">Separator Color</label>
//                 <ColorPicker
//                   color={content?.separatorColor || '#fbfbfb'}
//                   onChange={(color) => updateContent('separatorColor', color)}
//                 />
//               </div>
//             )}
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Particles Count</label>
//               <input
//                 type="number"
//                 min="0"
//                 max="50"
//                 value={content?.particlesCount !== undefined ? content.particlesCount : 15}
//                 onChange={(e) => updateContent('particlesCount', parseInt(e.target.value, 10))}
//                 className="w-full p-2 border rounded"
//               />
//               <p className="text-xs text-gray-500 mt-1">Set to 0 to disable particles</p>
//             </div>
            
//             {content?.particlesCount > 0 && (
//               <div className="mb-4">
//                 <label className="block text-sm font-medium mb-1">Particles Color</label>
//                 <ColorPicker
//                   color={content?.particlesColor || 'rgba(255, 255, 255, 0.2)'}
//                   onChange={(color) => updateContent('particlesColor', color)}
//                   allowAlpha={true}
//                 />
//               </div>
//             )}
            
//             <div className="mb-4">
//               <label className="flex items-center">
//                 <input
//                   type="checkbox"
//                   checked={content?.animationEnabled !== false}
//                   onChange={(e) => updateContent('animationEnabled', e.target.checked)}
//                   className="mr-2"
//                 />
//                 <span className="text-sm">Enable Animations</span>
//               </label>
//             </div>
//           </div>
//         </>
//       )}
//     </div>
//   );
// };

// export default SlideShowHeaderWaveForm;








// // src/components/admin/page-management/section-forms/SlideShowHeaderWaveForm.js
// import React, { useState } from 'react';
// import { Info } from 'lucide-react';
// import ColorPicker from '../../shared/ColorPicker';
// import MediaLibrary from '../../shared/MediaUploader';

// const SlideShowHeaderWaveForm = ({ content, updateContent, pageSlug }) => {
//   const [showMediaLibrary, setShowMediaLibrary] = useState(false);
//   const [currentImageField, setCurrentImageField] = useState(null);

//   const handleSelectImage = (imageUrl) => {
//     if (currentImageField) {
//       updateContent(currentImageField, imageUrl);
//       setShowMediaLibrary(false);
//       setCurrentImageField(null);
//     }
//   };

//   const openMediaLibrary = (fieldName) => {
//     setCurrentImageField(fieldName);
//     setShowMediaLibrary(true);
//   };

//   return (
//     <div className="space-y-6">
//       <h3 className="text-lg font-semibold">Hero Banner Section</h3>
      
//       {showMediaLibrary ? (
//         <div className="bg-white rounded-lg shadow-lg p-4">
//           <div className="flex justify-between items-center mb-4">
//             <h4 className="text-lg font-medium">Media Library</h4>
//             <button 
//               onClick={() => setShowMediaLibrary(false)}
//               className="text-gray-500 hover:text-gray-700"
//             >
//               Close
//             </button>
//           </div>
//           <MediaLibrary 
//             onSelect={handleSelectImage} 
//             pageSlug={pageSlug} 
//           />
//         </div>
//       ) : (
//         <>
//           {/* Content and Text Settings */}
//           <div className="bg-gray-50 p-4 rounded-md">
//             <h4 className="font-medium mb-4">Content</h4>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Title</label>
//               <input
//                 type="text"
//                 value={content?.title || 'Paper Bag & Box <span>Custom Packaging</span>'}
//                 onChange={(e) => updateContent('title', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 Use &lt;span&gt;text&lt;/span&gt; to highlight parts of the title
//               </p>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Subtitle</label>
//               <textarea
//                 value={content?.subtitle || 'Elevate your brand with premium quality custom bags and boxes. Perfect for businesses of all sizes.'}
//                 onChange={(e) => updateContent('subtitle', e.target.value)}
//                 className="w-full p-2 border rounded"
//                 rows="3"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Highlight Color</label>
//               <ColorPicker
//                 color={content?.highlightColor || '#f59e0b'}
//                 onChange={(color) => updateContent('highlightColor', color)}
//               />
//             </div>
//           </div>

//           {/* Background Settings */}
//           <div className="bg-gray-50 p-4 rounded-md">
//             <h4 className="font-medium mb-4">Background Settings</h4>


//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Background Type</label>
//               <div className="flex space-x-4">
//                 <label className="inline-flex items-center">
//                   <input
//                     type="radio"
//                     name="backgroundType"
//                     checked={!content?.videoBackground}
//                     onChange={() => updateContent('videoBackground', false)}
//                     className="mr-2"
//                   />
//                   <span className="text-sm">Image</span>
//                 </label>
//                 <label className="inline-flex items-center">
//                   <input
//                     type="radio"
//                     name="backgroundType"
//                     checked={!!content?.videoBackground}
//                     onChange={() => updateContent('videoBackground', true)}
//                     className="mr-2"
//                   />
//                   <span className="text-sm">Video</span>
//                 </label>
//               </div>
//             </div>


//             {!content?.videoBackground ? (
//               // Image background options (your existing code)
//               <>
//                 <div className="mb-4">
//                   <label className="block text-sm font-medium mb-1">Desktop Background Image</label>
//                   {/* Existing image upload code */}
//                 </div>
                
//                 <div className="mb-4">
//                   <label className="block text-sm font-medium mb-1">Mobile Background Image</label>
//                   {/* Existing mobile image upload code */}
//                 </div>
//               </>
//             ) : (
//               // Video background options
//               <>
//                 <div className="mb-4">
//                   <label className="block text-sm font-medium mb-1">Desktop Background Video</label>
//                   <div className="flex items-center">
//                     <input
//                       type="text"
//                       value={content?.backgroundVideo || ''}
//                       onChange={(e) => updateContent('backgroundVideo', e.target.value)}
//                       className="w-full p-2 border rounded"
//                     />
//                     <button
//                       type="button"
//                       onClick={() => openMediaLibrary('backgroundVideo', 'video')}
//                       className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
//                     >
//                       Browse
//                     </button>
//                   </div>
//                   {content?.backgroundVideo && (
//                     <div className="mt-2 relative h-20 w-full overflow-hidden rounded border">
//                       <video 
//                         src={content.backgroundVideo} 
//                         className="h-full w-full object-cover"
//                         controls
//                       />
//                     </div>
//                   )}
//                 </div>
                
//                 <div className="mb-4">
//                   <label className="block text-sm font-medium mb-1">Mobile Background Video</label>
//                   <div className="flex items-center">
//                     <input
//                       type="text"
//                       value={content?.mobileBackgroundVideo || ''}
//                       onChange={(e) => updateContent('mobileBackgroundVideo', e.target.value)}
//                       className="w-full p-2 border rounded"
//                     />
//                     <button
//                       type="button"
//                       onClick={() => openMediaLibrary('mobileBackgroundVideo', 'video')}
//                       className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
//                     >
//                       Browse
//                     </button>
//                   </div>
//                   {content?.mobileBackgroundVideo && (
//                     <div className="mt-2 relative h-20 w-full overflow-hidden rounded border">
//                       <video 
//                         src={content.mobileBackgroundVideo} 
//                         className="h-full w-full object-cover"
//                         controls
//                       />
//                     </div>
//                   )}
//                 </div>
                
//                 <div className="mb-4">
//                   <label className="block text-sm font-medium mb-1">Video Poster Image (Fallback)</label>
//                   <div className="flex items-center">
//                     <input
//                       type="text"
//                       value={content?.videoPoster || ''}
//                       onChange={(e) => updateContent('videoPoster', e.target.value)}
//                       className="w-full p-2 border rounded"
//                     />
//                     <button
//                       type="button"
//                       onClick={() => openMediaLibrary('videoPoster')}
//                       className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
//                     >
//                       Browse
//                     </button>
//                   </div>
//                 </div>
                
//                 <div className="grid grid-cols-3 gap-4">
//                   <div className="mb-4">
//                     <label className="flex items-center">
//                       <input
//                         type="checkbox"
//                         checked={content?.videoAutoplay !== false}
//                         onChange={(e) => updateContent('videoAutoplay', e.target.checked)}
//                         className="mr-2"
//                       />
//                       <span className="text-sm">Autoplay</span>
//                     </label>
//                   </div>
//                   <div className="mb-4">
//                     <label className="flex items-center">
//                       <input
//                         type="checkbox"
//                         checked={content?.videoLoop !== false}
//                         onChange={(e) => updateContent('videoLoop', e.target.checked)}
//                         className="mr-2"
//                       />
//                       <span className="text-sm">Loop</span>
//                     </label>
//                   </div>
//                   <div className="mb-4">
//                     <label className="flex items-center">
//                       <input
//                         type="checkbox"
//                         checked={content?.videoMuted !== false}
//                         onChange={(e) => updateContent('videoMuted', e.target.checked)}
//                         className="mr-2"
//                       />
//                       <span className="text-sm">Muted</span>
//                     </label>
//                   </div>
//                 </div>
//               </>
//             )}
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Desktop Background Image</label>
//               <div className="flex items-center">
//                 <input
//                   type="text"
//                   value={content?.backgroundImage || '/images/s1.jpg'}
//                   onChange={(e) => updateContent('backgroundImage', e.target.value)}
//                   className="w-full p-2 border rounded"
//                 />
//                 <button
//                   type="button"
//                   onClick={() => openMediaLibrary('backgroundImage')}
//                   className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
//                 >
//                   Browse
//                 </button>
//               </div>
//               {content?.backgroundImage && (
//                 <div className="mt-2 relative h-20 w-full overflow-hidden rounded border">
//                   <img 
//                     src={content.backgroundImage} 
//                     alt="Background Preview" 
//                     className="h-full w-full object-cover"
//                   />
//                 </div>
//               )}
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Mobile Background Image</label>
//               <div className="flex items-center">
//                 <input
//                   type="text"
//                   value={content?.mobileBackgroundImage || '/images/s3.jpg'}
//                   onChange={(e) => updateContent('mobileBackgroundImage', e.target.value)}
//                   className="w-full p-2 border rounded"
//                 />
//                 <button
//                   type="button"
//                   onClick={() => openMediaLibrary('mobileBackgroundImage')}
//                   className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
//                 >
//                   Browse
//                 </button>
//               </div>
//               {content?.mobileBackgroundImage && (
//                 <div className="mt-2 relative h-20 w-full overflow-hidden rounded border">
//                   <img 
//                     src={content.mobileBackgroundImage} 
//                     alt="Mobile Background Preview" 
//                     className="h-full w-full object-cover"
//                   />
//                 </div>
//               )}
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Overlay Color</label>
//               <ColorPicker
//                 color={content?.overlayColor || 'rgba(0,0,0,0.5)'}
//                 onChange={(color) => updateContent('overlayColor', color)}
//                 allowAlpha={true}
//               />
//             </div>
            
//             <div className="grid grid-cols-2 gap-4 mb-4">
//               <div>
//                 <label className="block text-sm font-medium mb-1">Height</label>
//                 <input
//                   type="text"
//                   value={content?.height || '100vh'}
//                   onChange={(e) => updateContent('height', e.target.value)}
//                   className="w-full p-2 border rounded"
//                   placeholder="e.g., 100vh, 800px"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Min Height</label>
//                 <input
//                   type="text"
//                   value={content?.minHeight || '600px'}
//                   onChange={(e) => updateContent('minHeight', e.target.value)}
//                   className="w-full p-2 border rounded"
//                   placeholder="e.g., 600px"
//                 />
//               </div>
//             </div>
//           </div>
          
//           {/* Primary Button Settings */}
//           <div className="bg-gray-50 p-4 rounded-md">
//             <h4 className="font-medium mb-4">Primary Button</h4>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Text</label>
//               <input
//                 type="text"
//                 value={content?.primaryButtonText || 'Customize Products'}
//                 onChange={(e) => updateContent('primaryButtonText', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Link</label>
//               <input
//                 type="text"
//                 value={content?.primaryButtonLink || '/products'}
//                 onChange={(e) => updateContent('primaryButtonLink', e.target.value)}
//                 className="w-full p-2 border rounded"
//                 placeholder="e.g., /products"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Color</label>
//               <ColorPicker
//                 color={content?.primaryButtonColor || '#d97706'}
//                 onChange={(color) => updateContent('primaryButtonColor', color)}
//               />
//             </div>
//           </div>
          
//           {/* Secondary Button Settings */}
//           <div className="bg-gray-50 p-4 rounded-md">
//             <h4 className="font-medium mb-4">Secondary Button</h4>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Text</label>
//               <input
//                 type="text"
//                 value={content?.secondaryButtonText || 'Call to Order'}
//                 onChange={(e) => updateContent('secondaryButtonText', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//               <p className="text-xs text-gray-500 mt-1">Leave empty to hide the secondary button</p>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Link</label>
//               <input
//                 type="text"
//                 value={content?.secondaryButtonLink || 'tel:+18333333101'}
//                 onChange={(e) => updateContent('secondaryButtonLink', e.target.value)}
//                 className="w-full p-2 border rounded"
//                 placeholder="e.g., tel:+18333333101"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Phone Number</label>
//               <input
//                 type="text"
//                 value={content?.phoneNumber || '+18333333101'}
//                 onChange={(e) => updateContent('phoneNumber', e.target.value)}
//                 className="w-full p-2 border rounded"
//                 placeholder="e.g., +18333333101"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Color</label>
//               <ColorPicker
//                 color={content?.secondaryButtonColor || '#16a34a'}
//                 onChange={(color) => updateContent('secondaryButtonColor', color)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="flex items-center">
//                 <input
//                   type="checkbox"
//                   checked={content?.showPhoneIcon !== false}
//                   onChange={(e) => updateContent('showPhoneIcon', e.target.checked)}
//                   className="mr-2"
//                 />
//                 <span className="text-sm">Show Phone Icon</span>
//               </label>
//             </div>
//           </div>
          
//           {/* Advanced Settings */}
//           <div className="bg-gray-50 p-4 rounded-md">
//             <div className="flex justify-between items-center mb-4">
//               <h4 className="font-medium">Advanced Settings</h4>
//               <div className="text-blue-500">
//                 <Info size={16} />
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="flex items-center">
//                 <input
//                   type="checkbox"
//                   checked={content?.showWaveSeparator !== false}
//                   onChange={(e) => updateContent('showWaveSeparator', e.target.checked)}
//                   className="mr-2"
//                 />
//                 <span className="text-sm">Show Wave Separator</span>
//               </label>
//             </div>
            
//             {content?.showWaveSeparator !== false && (
//               <div className="mb-4">
//                 <label className="block text-sm font-medium mb-1">Separator Color</label>
//                 <ColorPicker
//                   color={content?.separatorColor || '#fbfbfb'}
//                   onChange={(color) => updateContent('separatorColor', color)}
//                 />
//               </div>
//             )}
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Particles Count</label>
//               <input
//                 type="number"
//                 min="0"
//                 max="50"
//                 value={content?.particlesCount !== undefined ? content.particlesCount : 15}
//                 onChange={(e) => updateContent('particlesCount', parseInt(e.target.value, 10))}
//                 className="w-full p-2 border rounded"
//               />
//               <p className="text-xs text-gray-500 mt-1">Set to 0 to disable particles</p>
//             </div>
            
//             {content?.particlesCount > 0 && (
//               <div className="mb-4">
//                 <label className="block text-sm font-medium mb-1">Particles Color</label>
//                 <ColorPicker
//                   color={content?.particlesColor || 'rgba(255, 255, 255, 0.2)'}
//                   onChange={(color) => updateContent('particlesColor', color)}
//                   allowAlpha={true}
//                 />
//               </div>
//             )}
            
//             <div className="mb-4">
//               <label className="flex items-center">
//                 <input
//                   type="checkbox"
//                   checked={content?.animationEnabled !== false}
//                   onChange={(e) => updateContent('animationEnabled', e.target.checked)}
//                   className="mr-2"
//                 />
//                 <span className="text-sm">Enable Animations</span>
//               </label>
//             </div>
//           </div>
//         </>
//       )}
//     </div>
//   );
// };

// export default SlideShowHeaderWaveForm;